/**
 * The song cutter: one od–do selection over a sound, marked the same way
 * whether its audio has arrived yet or not.
 *
 * mp3cut-style at heart — the handles, the time fields and the player share
 * one selection, so what the shopper marks is exactly what they hear and what
 * the worker later produces — and made for a thumb: handles with a
 * finger-wide grip, −/+ steps on each end (hold to keep stepping), "Sada" to
 * set an end where the song is playing, quick lengths, and "Cela pesma" to
 * go back to the whole thing.
 *
 * A YouTube song is cut before its own file is here: the cutter opens on a
 * placeholder wave over the song's known length and plays through YouTube's
 * player (`transport`). `setAudio` then draws the real wave and plays the
 * file instead — without moving anything the shopper set, and without cutting
 * off a song that is playing (the file takes over at the next pause). While
 * the file is on its way the cutter says so in one quiet line under the wave
 * (a thin bar, never a counting percentage): nothing here is waited for.
 *
 * The wave itself is honest about the effects: a fade-in draws as a rising
 * ramp, a fade-out as a falling one, and the volume scales the bars — the
 * shopper sees the shape of the file they will receive, not a generic picture.
 */

import { observeSize } from './layers.js';

export interface AudioEffects {
  fadeInMs: number;
  fadeOutMs: number;
  volumePercent: number;
}

/**
 * Something else that plays the song while its own file is not here —
 * YouTube's player. Positions are in ms of the song.
 */
export interface CutterTransport {
  /** False while it cannot play (still loading). */
  ready(): boolean;
  playing(): boolean;
  positionMs(): number;
  play(): void;
  pause(): void;
  seek(ms: number): void;
  /** Runs `listener` when it starts, stops or becomes ready; returns the unsubscribe. */
  subscribe(listener: () => void): () => void;
}

/** The song's own file: its wave (or the file to draw it from) and where it is served. */
export interface CutterAudio {
  peaks?: number[];
  durationMs?: number;
  audioUrl?: string;
  blob?: Blob;
}

/**
 * A line about the song's own file: on its way (`busy` — a quiet line under
 * the wave, `percent` only moves its thin bar), here, or not coming.
 */
export interface CutterStatus {
  text: string;
  percent?: number;
  tone?: 'busy' | 'done' | 'note';
}

export interface WaveformTrimmer {
  element: HTMLElement;
  /** Cleans up object URLs, audio resources and observers. */
  destroy: () => void;
  /** Stops the sound if it is playing (the popup closed, another player started). */
  pause: () => void;
  /** The song's own file arrived: its wave and its sound, the part kept. False when it could not be read. */
  setAudio: (audio: CutterAudio) => Promise<boolean>;
  /** Another player for the song, or none (YouTube refused it). */
  setTransport: (transport: CutterTransport | null) => void;
  setStatus: (status: CutterStatus | null) => void;
  /** Whether the song's own file is here. */
  hasAudio: () => boolean;
  /**
   * Back to the whole sound ("Cela pesma" on the card), reported as no cut.
   * False — and nothing moves — when the whole is longer than the product plays.
   */
  setWhole: () => boolean;
  /** Whether the whole sound fits what the product plays (see {@link wholeFits}). */
  wholeFits: () => boolean;
  /** Redraws at the width it has now: call it when a cutter built out of sight comes into view. */
  relayout: () => void;
}

export interface WaveformOptions {
  blob?: Blob;
  /** Server-prepared alternative to a blob. */
  remote?: { peaks: number[]; durationMs: number; audioUrl: string };
  /** No audio yet, only its length (a YouTube song on its way): see `setAudio`. */
  pending?: { durationMs: number };
  /** Plays the song until its own file is here. */
  transport?: CutterTransport | null;
  onRange: (fromMs: number | undefined, toMs: number | undefined) => void;
  onEffects?: (effects: AudioEffects) => void;
  initialEffects?: Partial<AudioEffects>;
  /**
   * The part already chosen elsewhere (a song cut before its audio arrived),
   * so the cutter opens on it instead of starting over.
   */
  initialRange?: { fromMs?: number; toMs?: number };
  /** Fade lengths offered in the pickers, ms. */
  fadeChoicesMs?: number[];
  /** The length a freshly toggled fade gets, ms. */
  defaultFadeMs?: number;
  /** The shopper started playback here: every other player on the step stops. */
  onPlay?: () => void;
  /**
   * The whole song means "no cut": the range reports undefined at both ends,
   * so the worker keeps the song as long as its file really is.
   */
  wholeSong?: boolean;
  /** What the whole is called: "Cela pesma", "Ceo snimak". */
  wholeLabel?: string;
  /** The most the product plays, ms: a longer part is said to be too long. */
  maxMs?: number;
  /** A sound longer than `maxMs` opens on its first allowed stretch, with a word why. */
  fitToMax?: boolean;
  /** The file took over from the transport, which is not needed any more. */
  onAudioTakesOver?: () => void;
}

const TEXTS = {
  start: 'Početak',
  end: 'Kraj',
  earlier: (label: string, step: string) => `${label} ${step} ranije`,
  later: (label: string, step: string) => `${label} ${step} kasnije`,
  now: 'Sada',
  nowTitle: (label: string) => `${label}: tamo gde je puštanje sada`,
  whole: 'Ceo snimak',
  picked: (length: string) => `Izabrano: ${length}`,
  lengths: 'Dužina:',
  play: 'Pusti',
  playPart: 'Preslušaj deo',
  pause: 'Pauza',
  waiting: 'Zvuk se učitava…',
  silent: 'Ovde ne može da se pusti',
  hint: 'Pusti i pritisni „Sada“ tamo gde deo počinje, pa tamo gde se završava — ili pomeri ručice.',
  waveComing: 'Talas stiže za koji trenutak — deo već možeš da biraš.',
  zoomIn: 'Uvećaj talas za precizniji rez',
  zoomOut: 'Umanji talas',
  region: 'Izabrani deo pesme',
  notLoaded: 'Pesma se nije učitala.',
  playFailed: 'Preslušavanje nije uspelo.',
  tooLongWhole: (all: string, max: string) =>
    `Traje ${all}, a proizvod pušta najviše ${max}. Izabrali smo prvih ${max} — pomeri po želji.`,
  tooLongPart: (max: string) => `Izabrani deo je duži od ${max} koliko proizvod pušta — skrati ga.`,
};

const FADE_CHOICES_MS = [500, 1000, 2000, 3000, 5000];
/** Quick lengths, from the part's start. */
const LENGTH_CHIPS_MS = [15_000, 30_000, 60_000, 120_000];
/** Room each side of the time axis, so a handle at 0:00 or at the end is still a thumb wide. */
const PAD = 18;
/** A handle's grip: this wide, reaching this far into the part (the rest lies outside it). */
const GRIP_W = 36;
const GRIP_IN = 8;
/** The shortest part, ms. */
const MIN_PART_MS = 250;
const EPS = 0.0005;

const PLAY_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l11-6.5z" fill="currentColor"/></svg>';
const PAUSE_ICON =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" fill="currentColor"/></svg>';
const HERE_ICON =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 3v14M6.5 6.5 10 3l3.5 3.5"/><path d="M4 17h12"/></svg>';
const REPLAY_ICON =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 10a6 6 0 1 0 2-4.5"/><path d="M4 3.5V7h3.5"/></svg>';

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

/** How far past the product's maximum a whole sound may run and still count as fitting (a search length is approximate). */
const FIT_SLACK_MS = 500;

/**
 * Whether a whole sound of `durationMs` may be the answer as it is: the
 * product plays at most `maxMs` (none = no limit). A longer one is cut, and
 * the cutter opens on its first allowed stretch (`fitToMax`).
 */
export function wholeFits(durationMs: number, maxMs: number | undefined): boolean {
  return !maxMs || maxMs <= 0 || !(durationMs > maxMs + FIT_SLACK_MS);
}

/**
 * Live cutters, the one claimed last (built, touched or brought into view) at
 * the end. The spacebar belongs to the last one that is on screen: a cutter
 * folded away behind "Iseci deo", or on another step, never takes it.
 */
const cutterStack: Array<{ shown: () => boolean }> = [];

function claimSpace(entry: { shown: () => boolean }): void {
  const at = cutterStack.indexOf(entry);
  if (at >= 0) cutterStack.splice(at, 1);
  cutterStack.push(entry);
}

function releaseSpace(entry: { shown: () => boolean }): void {
  const at = cutterStack.indexOf(entry);
  if (at >= 0) cutterStack.splice(at, 1);
}

/** The cutter the spacebar plays and pauses now, if any. */
function spaceOwner(): { shown: () => boolean } | null {
  for (let i = cutterStack.length - 1; i >= 0; i--) if (cutterStack[i]!.shown()) return cutterStack[i]!;
  return null;
}

/** m:ss, or m:ss.s with `precise`. */
export function formatTime(ms: number, precise = false): string {
  const safe = Math.max(0, ms);
  const total = safe / 1000;
  if (!precise) {
    const whole = Math.round(total);
    return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
  }
  // Tenths, rounded first: 59.96 s is "1:00.0", never "0:60.0".
  const tenths = Math.round(total * 10);
  const minutes = Math.floor(tenths / 600);
  const seconds = (tenths - minutes * 600) / 10;
  return `${minutes}:${seconds.toFixed(1).padStart(4, '0')}`;
}

/** "1:05", "65", "1:05.5" or "1:05,5" → ms. */
export function parseTime(value: string): number | undefined {
  const parts = value.trim().replace(',', '.').split(':');
  if (parts.length > 2 || parts.some((part) => part.trim() === '')) return undefined;
  const seconds = parts.length === 2 ? Number(parts[0]) * 60 + Number(parts[1]) : Number(parts[0]);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : undefined;
}

/** "15 s", "1 min" */
function lengthWord(ms: number): string {
  return ms >= 60_000 && ms % 60_000 === 0 ? `${ms / 60_000} min` : `${Math.round(ms / 1000)} s`;
}

/** Peak buckets from raw samples, for the local-blob path. */
function computePeaks(channel: Float32Array, buckets: number): number[] {
  const per = Math.floor(channel.length / buckets) || 1;
  const peaks: number[] = [];
  for (let i = 0; i < buckets; i++) {
    let peak = 0;
    const from = i * per;
    // Sampling every eighth value is enough for a faithful screen waveform.
    for (let j = from; j < from + per; j += 8) {
      const value = Math.abs(channel[j] ?? 0);
      if (value > peak) peak = value;
    }
    peaks.push(peak);
  }
  return peaks;
}

async function decodeBlob(blob: Blob): Promise<{ peaks: number[]; durationMs: number } | null> {
  try {
    const AudioContextCtor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return null;
    const context = new AudioContextCtor();
    try {
      const decoded = await context.decodeAudioData(await blob.arrayBuffer());
      return { peaks: computePeaks(decoded.getChannelData(0), 1200), durationMs: decoded.duration * 1000 };
    } finally {
      void context.close();
    }
  } catch {
    return null;
  }
}

/** A calm stand-in wave while the real one is on its way; the same every time. */
function ghostLevel(i: number): number {
  return 0.22 + 0.5 * Math.abs(Math.sin(i * 0.37) * Math.cos(i * 0.113)) + 0.14 * Math.abs(Math.sin(i * 1.7));
}

// ── The song's own file, as a player ─────────────────────────────────────────

interface FilePlayer {
  playing(): boolean;
  positionMs(): number;
  /**
   * Seeks — waiting for the browser to finish — and plays at `level` (the
   * volume and fades where it starts); rejects when it cannot.
   */
  playFrom(ms: number, level: number): Promise<void>;
  pause(): void;
  seek(ms: number): void;
  /**
   * The volume and the fades as heard: above 100 % only through WebAudio.
   * Glided unless `immediate`, so a fade stepping every few frames stays smooth.
   */
  setGain(multiplier: number, immediate?: boolean): void;
  destroy(): void;
}

function createFilePlayer(url: string, revoke: boolean, onChange: () => void): FilePlayer {
  const audio = new Audio();
  // Metadata only: with several cutters open at once, 'auto' would buffer every
  // MP3 in full before anything is even played.
  audio.preload = 'metadata';
  audio.crossOrigin = 'anonymous';
  audio.src = url;
  let context: AudioContext | undefined;
  let gain: GainNode | undefined;
  for (const type of ['play', 'pause', 'ended']) audio.addEventListener(type, onChange);

  const ensureGraph = async (): Promise<void> => {
    if (!gain) {
      try {
        const AudioContextCtor =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (AudioContextCtor) {
          context = new AudioContextCtor();
          const source = context.createMediaElementSource(audio);
          gain = context.createGain();
          source.connect(gain).connect(context.destination);
        }
      } catch {
        // Cross-origin storage without media CORS cannot enter WebAudio. Playback
        // still works; only amplification above 100% is unavailable in that case.
      }
    }
    if (context?.state === 'suspended') await context.resume();
  };

  const waitForMetadata = (): Promise<void> => {
    if (audio.readyState >= HTMLMediaElement.HAVE_METADATA) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => finish(new Error(TEXTS.notLoaded)), 8000);
      const finish = (error?: Error): void => {
        window.clearTimeout(timeout);
        audio.removeEventListener('loadedmetadata', loaded);
        audio.removeEventListener('error', failed);
        if (error) reject(error);
        else resolve();
      };
      const loaded = (): void => finish();
      const failed = (): void => finish(new Error(TEXTS.notLoaded));
      audio.addEventListener('loadedmetadata', loaded, { once: true });
      audio.addEventListener('error', failed, { once: true });
    });
  };

  const setGain = (multiplier: number, immediate = false): void => {
    if (gain && context) {
      const now = context.currentTime;
      gain.gain.cancelScheduledValues(now);
      if (immediate) {
        gain.gain.setValueAtTime(multiplier, now);
      } else {
        gain.gain.setValueAtTime(gain.gain.value, now);
        gain.gain.linearRampToValueAtTime(multiplier, now + 0.045);
      }
    } else audio.volume = clamp(multiplier, 0, 1);
  };

  return {
    playing: () => !audio.paused && !audio.ended,
    positionMs: () => audio.currentTime * 1000,
    playFrom: async (ms, level) => {
      await Promise.all([waitForMetadata(), ensureGraph()]);
      // The level where it starts, set before a sound is heard: a fade-in
      // begins silent, not with a burst.
      setGain(level, true);
      // Seek before play and wait for the browser to finish seeking: on some
      // remote MP3s, play() right after assigning currentTime started at zero.
      const seconds = ms / 1000;
      if (Math.abs(audio.currentTime - seconds) > 0.02) {
        audio.currentTime = seconds;
        await new Promise<void>((resolve) => {
          const timeout = window.setTimeout(resolve, 2500);
          audio.addEventListener(
            'seeked',
            () => {
              window.clearTimeout(timeout);
              resolve();
            },
            { once: true },
          );
        });
      }
      await audio.play();
    },
    pause: () => {
      if (!audio.paused) audio.pause();
    },
    seek: (ms) => {
      if (audio.readyState < HTMLMediaElement.HAVE_METADATA) return;
      try {
        audio.currentTime = ms / 1000;
      } catch {
        // Some browsers reject seeks until the first metadata event.
      }
    },
    setGain,
    destroy: () => {
      for (const type of ['play', 'pause', 'ended']) audio.removeEventListener(type, onChange);
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      if (revoke) URL.revokeObjectURL(url);
      void context?.close();
    },
  };
}

// ── The cutter ──────────────────────────────────────────────────────────────

interface LoadedAudio {
  peaks: number[];
  durationMs: number;
  url: string;
  revoke: boolean;
}

/**
 * A cutter over a file (`blob` or `remote`), or — with `pending` — over a
 * song whose file is still on its way. Null when the file cannot be read.
 */
export async function createWaveformTrimmer(options: WaveformOptions): Promise<WaveformTrimmer | null> {
  if (options.remote) {
    const { peaks, durationMs, audioUrl } = options.remote;
    if (!peaks.length || !durationMs) return null;
    return buildCutter(options, { peaks, durationMs, url: audioUrl, revoke: false });
  }
  if (options.blob) {
    const decoded = await decodeBlob(options.blob);
    if (!decoded) return null;
    return buildCutter(options, { ...decoded, url: URL.createObjectURL(options.blob), revoke: true });
  }
  return createPendingCutter(options);
}

/** The cutter for a song whose file is on its way: at once, over its known length. */
export function createPendingCutter(options: WaveformOptions): WaveformTrimmer | null {
  if (!options.pending || !(options.pending.durationMs > 0)) return null;
  return buildCutter(options, null);
}

function buildCutter(options: WaveformOptions, loaded: LoadedAudio | null): WaveformTrimmer {
  const { onRange } = options;
  const wholeSong = options.wholeSong === true;
  const wholeLabel = options.wholeLabel?.trim() || TEXTS.whole;
  const maxMs = options.maxMs && options.maxMs > 0 ? options.maxMs : undefined;

  let durationMs = loaded?.durationMs ?? options.pending?.durationMs ?? 0;
  let peaks: number[] | null = loaded?.peaks ?? null;
  let file: FilePlayer | null = null;
  let transport: CutterTransport | null = null;
  let unsubscribe: (() => void) | null = null;
  /** The file is here, but the song is playing on YouTube: it takes over at the next pause. */
  let switchWhenIdle = false;
  let destroyed = false;
  let status: CutterStatus | null = null;

  // ── The markup ────────────────────────────────────────────────────────────
  const root = document.createElement('div');
  root.className = 'pz-cutter';

  // A slim lane over the wave: how long the part is, and the zoom that
  // magnifies the wave (map-style, beside what it magnifies).
  const lane = document.createElement('div');
  lane.className = 'pz-cut-lane';
  const picked = document.createElement('span');
  picked.className = 'pz-cut-picked';
  const zoomControls = document.createElement('div');
  zoomControls.className = 'pz-wave-zoom';
  const zoomOut = document.createElement('button');
  zoomOut.type = 'button';
  zoomOut.className = 'pz-wave-zoom-btn';
  zoomOut.textContent = '−';
  zoomOut.title = TEXTS.zoomOut;
  zoomOut.setAttribute('aria-label', TEXTS.zoomOut);
  const zoomIn = document.createElement('button');
  zoomIn.type = 'button';
  zoomIn.className = 'pz-wave-zoom-btn';
  zoomIn.textContent = '+';
  zoomIn.title = TEXTS.zoomIn;
  zoomIn.setAttribute('aria-label', TEXTS.zoomIn);
  zoomControls.append(zoomOut, zoomIn);
  lane.append(picked, zoomControls);

  // The scroller holds a track that grows with zoom; the wave lives on the track.
  const scroller = document.createElement('div');
  scroller.className = 'pz-wave-scroll';
  const track = document.createElement('div');
  track.className = 'pz-wave';
  const canvas = document.createElement('canvas');
  const shadeLeft = document.createElement('div');
  shadeLeft.className = 'shade';
  const shadeRight = document.createElement('div');
  shadeRight.className = 'shade';
  const region = document.createElement('div');
  region.className = 'region';
  region.setAttribute('aria-label', TEXTS.region);
  const gripLeft = document.createElement('div');
  gripLeft.className = 'grip grip-start';
  const gripRight = document.createElement('div');
  gripRight.className = 'grip grip-end';
  const bubbleLeft = document.createElement('span');
  bubbleLeft.className = 'bubble';
  const bubbleRight = document.createElement('span');
  bubbleRight.className = 'bubble';
  const playhead = document.createElement('div');
  playhead.className = 'playhead';
  playhead.hidden = true;
  // The real wave on its way: a soft shimmer over the stand-in (no words on
  // it), and one quiet line under the wave — see `setStatus`.
  const veil = document.createElement('div');
  veil.className = 'pz-wave-veil';
  veil.setAttribute('aria-hidden', 'true');
  veil.hidden = true;
  track.append(canvas, shadeLeft, shadeRight, region, gripLeft, gripRight, bubbleLeft, bubbleRight, playhead, veil);
  scroller.appendChild(track);
  const waveWrap = document.createElement('div');
  waveWrap.className = 'pz-wave-wrap';
  waveWrap.appendChild(scroller);
  const coming = document.createElement('p');
  coming.className = 'pz-cut-coming';
  coming.setAttribute('role', 'status');
  coming.hidden = true;
  const comingText = document.createElement('span');
  const comingBar = document.createElement('span');
  comingBar.className = 'pz-cut-coming-bar';
  comingBar.setAttribute('aria-hidden', 'true');
  const comingFill = document.createElement('i');
  comingBar.appendChild(comingFill);
  coming.append(comingText, comingBar);

  // Play/pause, where the song is, and "Preslušaj deo".
  const transportRow = document.createElement('div');
  transportRow.className = 'pz-cut-transport';
  const playButton = document.createElement('button');
  playButton.type = 'button';
  playButton.className = 'pz-play-selection';
  const clockEl = document.createElement('span');
  clockEl.className = 'pz-cut-clock';
  const previewButton = document.createElement('button');
  previewButton.type = 'button';
  previewButton.className = 'pz-btn-ghost small pz-cut-preview';
  previewButton.innerHTML = `${REPLAY_ICON}<span>${TEXTS.playPart}</span>`;
  transportRow.append(playButton, clockEl, previewButton);

  // The two ends, each with its time, its steps and "Sada".
  const ends = document.createElement('div');
  ends.className = 'pz-cut-ends';

  interface EndControl {
    box: HTMLElement;
    input: HTMLInputElement;
    minus: HTMLButtonElement;
    plus: HTMLButtonElement;
    now: HTMLButtonElement;
    nowText: HTMLElement;
  }
  const makeEnd = (label: string): EndControl => {
    const box = document.createElement('div');
    box.className = 'pz-cut-end';
    const caption = document.createElement('span');
    caption.className = 'pz-cut-end-label';
    caption.textContent = label;
    const stepper = document.createElement('div');
    stepper.className = 'pz-cut-stepper';
    const minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'pz-cut-nudge';
    minus.textContent = '−';
    const input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'decimal';
    input.className = 'pz-cut-time';
    input.setAttribute('aria-label', label);
    input.autocomplete = 'off';
    const plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'pz-cut-nudge';
    plus.textContent = '+';
    stepper.append(minus, input, plus);
    const now = document.createElement('button');
    now.type = 'button';
    now.className = 'pz-cut-here';
    now.title = TEXTS.nowTitle(label);
    const nowText = document.createElement('span');
    now.innerHTML = HERE_ICON;
    now.appendChild(nowText);
    box.append(caption, stepper, now);
    return { box, input, minus, plus, now, nowText };
  };
  const startEnd = makeEnd(TEXTS.start);
  const endEnd = makeEnd(TEXTS.end);
  ends.append(startEnd.box, endEnd.box);

  // Quick lengths: the whole, or so long from the part's start.
  const lengths = document.createElement('div');
  lengths.className = 'pz-cut-lengths';

  const hint = document.createElement('p');
  hint.className = 'pz-hint pz-cut-hint';
  hint.textContent = TEXTS.hint;
  hint.hidden = true;
  const warn = document.createElement('p');
  warn.className = 'pz-cut-warn';
  warn.hidden = true;
  const note = document.createElement('p');
  note.className = 'pz-cut-note';
  note.hidden = true;
  const failure = document.createElement('p');
  failure.className = 'pz-cut-status';
  failure.hidden = true;

  // ── Effects: fades with length pickers, volume ────────────────────────────
  const effects = document.createElement('div');
  effects.className = 'pz-audio-effects';
  const effectButtons = document.createElement('div');
  effectButtons.className = 'pz-effect-buttons';
  const fadeChoices = (options.fadeChoicesMs?.length ? options.fadeChoicesMs : FADE_CHOICES_MS)
    .filter((ms) => Number.isFinite(ms) && ms > 0)
    .sort((a, b) => a - b);
  const defaultFade = options.defaultFadeMs && fadeChoices.includes(options.defaultFadeMs)
    ? options.defaultFadeMs
    : (fadeChoices.find((ms) => ms >= 2000) ?? fadeChoices[0] ?? 2000);

  /** A fade toggle with its own length picker, the way mp3cut arranges it. */
  const makeFade = (
    direction: 'in' | 'out',
    label: string,
  ): { wrap: HTMLElement; button: HTMLButtonElement; select: HTMLSelectElement } => {
    const wrap = document.createElement('div');
    wrap.className = 'pz-fade-group';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pz-effect-btn';
    button.innerHTML = `<span class="pz-fade-icon is-${direction}"></span><span>${label}</span>`;
    const select = document.createElement('select');
    select.className = 'pz-fade-length';
    select.setAttribute('aria-label', `Trajanje (${label.toLowerCase()})`);
    for (const ms of fadeChoices) {
      const item = document.createElement('option');
      item.value = String(ms);
      item.textContent = ms % 1000 === 0 ? `${ms / 1000} s` : `${(ms / 1000).toFixed(1)} s`;
      select.appendChild(item);
    }
    select.value = String(defaultFade);
    wrap.append(button, select);
    return { wrap, button, select };
  };
  const fadeInCtl = makeFade('in', 'Fade in');
  const fadeOutCtl = makeFade('out', 'Fade out');
  const fadeIn = fadeInCtl.button;
  const fadeOut = fadeOutCtl.button;
  effectButtons.append(fadeInCtl.wrap, fadeOutCtl.wrap);

  const volume = document.createElement('label');
  volume.className = 'pz-volume';
  const volumeTitle = document.createElement('span');
  volumeTitle.textContent = 'Volume';
  const volumeInput = document.createElement('input');
  volumeInput.type = 'range';
  volumeInput.min = '10';
  volumeInput.max = '250';
  volumeInput.step = '5';
  const volumeValue = document.createElement('output');
  volume.append(volumeTitle, volumeInput, volumeValue);
  effects.append(effectButtons, volume);
  // Shown from the start: the fades and the volume are what the worker applies
  // to the delivered file, so they can be set before the file is here (they
  // are heard on it once it is; YouTube's own player is never turned up or down).

  root.append(lane, waveWrap, coming, transportRow, ends, lengths, hint, warn, note, failure, effects);

  // ── The selection ─────────────────────────────────────────────────────────
  // Stored as a fraction of the whole source; zoom only stretches its
  // presentation and cannot alter the actual times.
  let start = 0;
  let end = 1;
  if (options.initialRange && durationMs > 0) {
    const from = options.initialRange.fromMs;
    const to = options.initialRange.toMs;
    if (from != null && Number.isFinite(from)) start = clamp(from / durationMs, 0, 0.99);
    if (to != null && Number.isFinite(to) && to > (from ?? 0)) end = clamp(to / durationMs, start + 0.001, 1);
  }
  let zoom = 1;
  let effectsState: AudioEffects = {
    fadeInMs: clamp(options.initialEffects?.fadeInMs ?? 0, 0, 10_000),
    fadeOutMs: clamp(options.initialEffects?.fadeOutMs ?? 0, 0, 10_000),
    volumePercent: clamp(options.initialEffects?.volumePercent ?? 100, 10, 250),
  };

  const msAt = (fraction: number): number => fraction * durationMs;
  const minPart = (): number => Math.min(0.5, MIN_PART_MS / Math.max(1, durationMs));
  const isWhole = (): boolean => start <= EPS && end >= 1 - EPS;
  /** The step of one −/+ tap: a second on a song, finer on a short message. */
  const stepMs = (): number => (durationMs > 90_000 ? 1000 : durationMs > 20_000 ? 500 : 100);
  const stepWord = (): string => {
    const step = stepMs();
    return step >= 1000 ? `${step / 1000} s` : `${String(step / 1000).replace('.', ',')} s`;
  };

  /** A sound longer than the product plays opens on its first allowed stretch. */
  let fittedNote = '';
  if (options.fitToMax && maxMs && !wholeFits(durationMs, maxMs) && isWhole()) {
    end = maxMs / durationMs;
    fittedNote = TEXTS.tooLongWhole(formatTime(durationMs), formatTime(maxMs));
  }

  /** Tells the order what the part is now; a change of the shopper's own retires the "too long" note. */
  const report = (byShopper = true): void => {
    if (byShopper && fittedNote) {
      fittedNote = '';
      layout();
    }
    // The whole song is no cut at all: the worker keeps it as long as its file
    // really is, which a length from a search result only approximates.
    if (wholeSong && isWhole()) onRange(undefined, undefined);
    // Always both visible markers otherwise. This avoids losing "0:45 to the
    // end" when the full-duration endpoint would be represented as undefined.
    else onRange(Math.round(msAt(start)), Math.round(msAt(end)));
  };

  // ── Geometry ──────────────────────────────────────────────────────────────
  const trackWidth = (): number => Math.max(PAD * 2 + 60, scroller.clientWidth * zoom);
  const xAt = (fraction: number): number => PAD + fraction * (trackWidth() - PAD * 2);
  const fractionAt = (clientX: number): number => {
    const rect = track.getBoundingClientRect();
    const axis = rect.width - PAD * 2;
    return clamp((clientX - rect.left - PAD) / (axis > 0 ? axis : 1), 0, 1);
  };

  /**
   * How tall a bar draws at a given position: the shopper's volume scales the
   * whole selection, and the fades ramp it at the edges — exactly the envelope
   * ffmpeg applies to the delivered file.
   */
  const envelopeAt = (fraction: number): number => {
    if (fraction < start || fraction > end) return 1;
    let factor = clamp(effectsState.volumePercent / 100, 0.15, 1.4);
    const positionMs = msAt(fraction);
    const fromMs = msAt(start);
    const toMs = msAt(end);
    if (effectsState.fadeInMs > 0) factor *= clamp((positionMs - fromMs) / effectsState.fadeInMs, 0, 1);
    if (effectsState.fadeOutMs > 0) factor *= clamp((toMs - positionMs) / effectsState.fadeOutMs, 0, 1);
    return factor;
  };

  const draw = (): void => {
    const width = Math.max(100, Math.floor(trackWidth()));
    const height = Math.floor(track.clientHeight) || 76;
    track.style.width = `${width}px`;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.scale(ratio, ratio);
    context.clearRect(0, 0, width, height);
    const axis = width - PAD * 2;
    const middle = height / 2;
    // Headroom for the display's volume ceiling, so louder never clips the box.
    const budget = (height - 20) / 1.4;
    if (peaks) {
      const gradient = context.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, '#8b80ef');
      gradient.addColorStop(0.5, '#5d50d6');
      gradient.addColorStop(1, '#8b80ef');
      context.fillStyle = gradient;
      const max = peaks.reduce((a, b) => Math.max(a, b), 0) || 1;
      for (let x = 0; x < axis; x++) {
        const peak = (peaks[Math.floor((x / axis) * peaks.length)] ?? 0) / max;
        const barHeight = Math.max(2, peak * budget * envelopeAt(x / axis));
        context.fillRect(PAD + x, middle - barHeight / 2, 1, barHeight);
      }
    } else {
      // The real wave is on its way: a quiet stand-in, so the part can be
      // chosen on the song's length already.
      context.fillStyle = '#d9d7e7';
      for (let x = 0; x < axis; x += 3) {
        const barHeight = Math.max(2, ghostLevel(Math.round(x / 3)) * budget);
        context.fillRect(PAD + x, middle - barHeight / 2, 2, barHeight);
      }
    }
    // A hairline through the middle keeps quiet passages readable.
    context.fillStyle = 'rgba(101,88,220,.35)';
    context.fillRect(PAD, middle - 0.5, axis, 1);
  };

  const focused = (el: HTMLElement): boolean => {
    try {
      return el.matches(':focus');
    } catch {
      return false;
    }
  };

  const layout = (): void => {
    const width = trackWidth();
    const xs = xAt(start);
    const xe = xAt(end);
    shadeLeft.style.left = '0';
    shadeLeft.style.width = `${xs}px`;
    shadeRight.style.left = `${xe}px`;
    shadeRight.style.width = `${Math.max(0, width - xe)}px`;
    region.style.left = `${xs}px`;
    region.style.width = `${Math.max(0, xe - xs)}px`;
    gripLeft.style.left = `${xs - (GRIP_W - GRIP_IN)}px`;
    gripRight.style.left = `${xe - GRIP_IN}px`;
    const precise = zoom > 1.5;
    bubbleLeft.textContent = formatTime(msAt(start), precise);
    bubbleRight.textContent = formatTime(msAt(end), precise);
    // The times sit inside the part while it is wide enough for both, and
    // step outside it when it is not, so they never cover each other.
    const wl = bubbleLeft.offsetWidth || 34;
    const wr = bubbleRight.offsetWidth || 34;
    const inside = xe - xs >= wl + wr + 14;
    bubbleLeft.style.left = `${inside ? xs + 5 : Math.max(2, xs - 5 - wl)}px`;
    bubbleRight.style.left = `${inside ? xe - 5 - wr : Math.min(width - wr - 2, xe + 5)}px`;

    if (!focused(startEnd.input)) startEnd.input.value = formatTime(msAt(start), true);
    if (!focused(endEnd.input)) endEnd.input.value = formatTime(msAt(end), true);
    startEnd.minus.disabled = start <= EPS;
    startEnd.plus.disabled = start >= end - minPart() - EPS;
    endEnd.minus.disabled = end <= start + minPart() + EPS;
    endEnd.plus.disabled = end >= 1 - EPS;

    const partMs = msAt(end) - msAt(start);
    picked.textContent = isWhole() ? `${wholeLabel} · ${formatTime(durationMs)}` : TEXTS.picked(formatTime(partMs));
    root.classList.toggle('is-whole', isWhole());
    previewButton.hidden = isWhole();
    for (const chip of lengths.querySelectorAll<HTMLElement>('.pz-chip')) {
      const ms = Number(chip.dataset.ms);
      chip.classList.toggle('is-active', ms === 0 ? isWhole() : !isWhole() && Math.abs(partMs - ms) < 60);
    }

    const warning =
      fittedNote ||
      (maxMs && partMs > maxMs + 500 ? TEXTS.tooLongPart(formatTime(maxMs)) : '');
    warn.textContent = warning;
    warn.hidden = !warning;
  };

  /** The wave and the geometry together — cheap enough to run mid-drag. */
  const refresh = (): void => {
    draw();
    layout();
  };

  // ── Where the sound comes from ────────────────────────────────────────────
  type Source = 'file' | 'transport' | 'none';
  const source = (): Source => (transport ? 'transport' : file ? 'file' : 'none');
  const playingNow = (): boolean => {
    const from = source();
    return from === 'file' ? file!.playing() : from === 'transport' ? transport!.playing() : false;
  };
  const positionMs = (): number => {
    const from = source();
    return from === 'file' ? file!.positionMs() : from === 'transport' ? transport!.positionMs() : 0;
  };
  const canPlay = (): boolean => {
    const from = source();
    return from === 'file' || (from === 'transport' && transport!.ready());
  };

  const effectiveGain = (at: number = positionMs()): number => {
    let multiplier = effectsState.volumePercent / 100;
    if (effectsState.fadeInMs > 0) multiplier *= clamp((at - msAt(start)) / effectsState.fadeInMs, 0, 1);
    if (effectsState.fadeOutMs > 0) multiplier *= clamp((msAt(end) - at) / effectsState.fadeOutMs, 0, 1);
    return multiplier;
  };
  const applyGain = (immediate = false): void => {
    if (source() === 'file') file!.setGain(effectiveGain(), immediate);
  };

  // ── Playback ──────────────────────────────────────────────────────────────
  /**
   * `part`: started before the part's end, so it stops there. `free`: started
   * past it (or on YouTube's own controls), so it plays to the song's end.
   */
  let playMode: 'part' | 'free' = 'part';
  /** Where the playhead stands: a tap on the wave, or where it paused. Null = the part's start. */
  let parked: number | null = null;
  /** Our own play is on its way: its "playing" is ours, not YouTube's own controls. */
  let requested = false;
  let heard = false;
  /** Whether it was playing at the last look, so a pause can leave the playhead where it stopped. */
  let wasOn = false;
  /**
   * A timer, not animation frames: those stop in a hidden page, and a part
   * that plays on past its end (or a pause that forgets where it was) is the
   * wrong answer to "Preslušaj".
   */
  let ticker: number | undefined;

  const showPlayhead = (fraction: number): void => {
    playhead.hidden = false;
    playhead.style.left = `${xAt(clamp(fraction, 0, 1)) - 1}px`;
  };

  const pauseSource = (): void => {
    const from = source();
    if (from === 'file') file!.pause();
    else if (from === 'transport') transport!.pause();
  };

  /** Stops without parking: the playhead goes home (the part ended, a handle is being moved). */
  const silence = (): void => {
    wasOn = false;
    pauseSource();
    parked = null;
    playhead.hidden = true;
  };

  /** The part played to its end: the playhead goes home, ready to play it again. */
  const finishAtEnd = (): void => {
    silence();
    renderTransport();
  };

  const stopTicking = (): void => {
    window.clearInterval(ticker);
    ticker = undefined;
  };
  const tick = (): void => {
    if (destroyed) {
      stopTicking();
      return;
    }
    const on = playingNow();
    if (on && durationMs > 0) {
      const at = clamp(positionMs() / durationMs, 0, 1);
      parked = at;
      showPlayhead(at);
      applyGain();
      if ((playMode === 'part' && at >= end - EPS) || at >= 1 - EPS) {
        finishAtEnd();
        stopTicking();
        return;
      }
    }
    renderReadout();
    if (!on) stopTicking();
  };
  const kick = (): void => {
    if (ticker === undefined && !destroyed) ticker = window.setInterval(tick, 40);
    tick();
  };

  /** Plays from `from` (the parked playhead, else the part's start). */
  const startPlayback = async (from: number = parked ?? start): Promise<void> => {
    if (!canPlay()) return;
    options.onPlay?.();
    playMode = from < end - EPS ? 'part' : 'free';
    heard = true;
    parked = from;
    showPlayhead(from);
    failure.hidden = true;
    if (source() === 'file') {
      playButton.disabled = true;
      try {
        await file!.playFrom(msAt(from), effectiveGain(msAt(from)));
      } catch (error) {
        failure.textContent = error instanceof Error && error.message ? error.message : TEXTS.playFailed;
        failure.hidden = false;
      } finally {
        playButton.disabled = false;
      }
    } else {
      requested = true;
      transport!.seek(msAt(from));
      transport!.play();
    }
    renderTransport();
    kick();
  };

  /** Moves the playhead; a playing song carries on from there. */
  const seekTo = (fraction: number): void => {
    parked = clamp(fraction, 0, 1);
    showPlayhead(parked);
    const from = source();
    if (from === 'file') file!.seek(msAt(parked));
    else if (from === 'transport' && transport!.ready()) transport!.seek(msAt(parked));
    // Stop at the part's end when the new spot is inside it, else at the song's.
    if (playingNow()) playMode = parked < end - EPS ? 'part' : 'free';
    renderTransport();
  };

  /** Where "Sada" puts an end: where the song is, or where the playhead stands. */
  const here = (): number | null => {
    if (playingNow() && durationMs > 0) return clamp(positionMs() / durationMs, 0, 1);
    return parked;
  };

  /** Writes only a change: the readout is asked for many times a second while a song plays. */
  const setText = (el: HTMLElement, text: string): void => {
    if (el.textContent !== text) el.textContent = text;
  };
  const renderReadout = (): void => {
    const at = here();
    setText(clockEl, `${formatTime(msAt(at ?? start))} / ${formatTime(durationMs)}`);
    const now = at === null ? TEXTS.now : `${TEXTS.now} ${formatTime(msAt(at))}`;
    for (const control of [startEnd, endEnd]) {
      setText(control.nowText, now);
      if (control.now.disabled !== (at === null)) control.now.disabled = at === null;
    }
  };

  /** The play button, and what it says when there is nothing to play yet. */
  const renderTransport = (): void => {
    const on = playingNow();
    const from = source();
    const waiting = !on && (from === 'transport' ? !transport!.ready() : from === 'none' && status?.tone === 'busy');
    const silent = !on && from === 'none' && !waiting;
    playButton.innerHTML = on ? PAUSE_ICON : PLAY_ICON;
    playButton.classList.toggle('is-waiting', waiting);
    playButton.disabled = waiting || silent;
    const label = on ? TEXTS.pause : waiting ? TEXTS.waiting : silent ? TEXTS.silent : TEXTS.play;
    playButton.title = label;
    playButton.setAttribute('aria-label', label);
    previewButton.disabled = waiting || silent;
    root.classList.toggle('is-playing', on);
    // How "Sada" works is said once, where it helps: before anything was heard.
    hint.hidden = heard || !canPlay();
    renderReadout();
  };

  /** The file becomes the sound; the song's other player steps aside. */
  const takeOver = (): void => {
    switchWhenIdle = false;
    unsubscribe?.();
    unsubscribe = null;
    const had = transport !== null;
    transport = null;
    if (had) options.onAudioTakesOver?.();
    renderTransport();
  };

  const onSourceChange = (): void => {
    if (destroyed) return;
    let on = playingNow();
    if (wasOn && !on && durationMs > 0) {
      // Paused: the playhead stays where the song stopped — read from the
      // player that stopped, before the file takes over — for "Sada" and to
      // play on from there. A song that ran to its very end starts over.
      const at = clamp(positionMs() / durationMs, 0, 1);
      parked = at >= 1 - EPS ? null : at;
      if (parked === null) playhead.hidden = true;
      else showPlayhead(parked);
    }
    wasOn = on;
    if (switchWhenIdle && transport && !on) {
      takeOver();
      on = playingNow();
    }
    if (on) {
      // Started on YouTube's own controls (a tap on the video), or by
      // "Preslušaj sve": follow it to the song's end rather than cut it short.
      if (!requested && source() === 'transport') playMode = 'free';
      requested = false;
      heard = true;
    }
    renderTransport();
    kick();
  };

  const setTransport = (next: CutterTransport | null): void => {
    if (next === transport) return;
    unsubscribe?.();
    unsubscribe = null;
    switchWhenIdle = false;
    transport = next;
    if (transport) unsubscribe = transport.subscribe(onSourceChange);
    renderTransport();
  };

  playButton.addEventListener('click', () => {
    if (playingNow()) pauseSource();
    else void startPlayback();
  });
  previewButton.addEventListener('click', () => void startPlayback(start));

  // ── Changing the part ─────────────────────────────────────────────────────
  /** Sets one end, in ms; an end pushed past the other moves the other along, keeping the length. */
  const setEnd = (which: 'start' | 'end', ms: number): void => {
    const length = end - start;
    const min = minPart();
    const fraction = clamp(ms / durationMs, 0, 1);
    if (which === 'start') {
      start = Math.min(fraction, 1 - min);
      if (start > end - min) end = Math.min(1, start + Math.max(length, min));
    } else {
      end = Math.max(fraction, min);
      if (end < start + min) start = Math.max(0, end - Math.max(length, min));
    }
    refresh();
    report();
  };

  const nudge = (which: 'start' | 'end', direction: -1 | 1): void => {
    const min = minPart();
    const delta = (direction * stepMs()) / durationMs;
    if (which === 'start') start = clamp(start + delta, 0, end - min);
    else end = clamp(end + delta, start + min, 1);
    // A step that lands within a hair of the edge is the edge.
    if (start < EPS * 2) start = 0;
    if (end > 1 - EPS * 2) end = 1;
    refresh();
    report();
  };

  /**
   * A tap steps once; holding keeps stepping. The click that ends a hold is
   * swallowed, so a hold never takes one step too many.
   */
  const holdToRepeat = (button: HTMLButtonElement, act: () => void): void => {
    let delay: number | undefined;
    let timer: number | undefined;
    let repeated = false;
    const stop = (): void => {
      window.clearTimeout(delay);
      window.clearInterval(timer);
      delay = undefined;
      timer = undefined;
    };
    button.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      stop();
      repeated = false;
      delay = window.setTimeout(() => {
        repeated = true;
        act();
        timer = window.setInterval(() => {
          if (button.disabled) stop();
          else act();
        }, 110);
      }, 420);
    });
    for (const type of ['pointerup', 'pointerleave', 'pointercancel']) button.addEventListener(type, stop);
    // A long press must not open the phone's menu for the button.
    button.addEventListener('contextmenu', (event) => event.preventDefault());
    button.addEventListener('click', () => {
      if (repeated) {
        repeated = false;
        return;
      }
      act();
    });
  };

  /** The −/+ buttons name their step, which depends on the sound's length. */
  const relabels: Array<() => void> = [];
  for (const [which, control] of [
    ['start', startEnd],
    ['end', endEnd],
  ] as const) {
    const label = which === 'start' ? TEXTS.start : TEXTS.end;
    holdToRepeat(control.minus, () => nudge(which, -1));
    holdToRepeat(control.plus, () => nudge(which, 1));
    control.input.addEventListener('change', () => {
      const ms = parseTime(control.input.value);
      control.input.classList.toggle('is-invalid', ms == null);
      if (ms == null) return;
      setEnd(which, ms);
    });
    control.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        control.input.blur();
      }
    });
    control.input.addEventListener('focus', () => control.input.select());
    control.input.addEventListener('blur', () => {
      control.input.classList.remove('is-invalid');
      layout();
    });
    control.now.addEventListener('click', () => {
      const at = here();
      if (at === null) return;
      const playing = playingNow();
      setEnd(which, msAt(at));
      // The part now ends where the song is: it stops there, ready to be heard whole.
      if (which === 'end' && playing) playMode = 'part';
    });
    const refreshLabels = (): void => {
      const step = stepWord();
      control.minus.setAttribute('aria-label', TEXTS.earlier(label, step));
      control.minus.title = TEXTS.earlier(label, step);
      control.plus.setAttribute('aria-label', TEXTS.later(label, step));
      control.plus.title = TEXTS.later(label, step);
    };
    refreshLabels();
    relabels.push(refreshLabels);
  }

  const buildLengths = (): void => {
    lengths.innerHTML = '';
    // The three longest that are shorter than the sound: one row on a phone.
    const choices = LENGTH_CHIPS_MS.filter((ms) => ms < durationMs - 1000).slice(-3);
    const caption = document.createElement('span');
    caption.textContent = TEXTS.lengths;
    lengths.appendChild(caption);
    for (const ms of [0, ...choices]) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'pz-chip';
      chip.dataset.ms = String(ms);
      chip.textContent = ms === 0 ? wholeLabel : lengthWord(ms);
      chip.addEventListener('click', () => {
        if (ms === 0) {
          start = 0;
          end = 1;
        } else {
          // So long from the part's start; near the song's end the start moves back.
          const length = ms / durationMs;
          start = Math.min(start, Math.max(0, 1 - length));
          end = Math.min(1, start + length);
        }
        refresh();
        report();
      });
      lengths.appendChild(chip);
    }
  };

  // ── The wave, by hand ─────────────────────────────────────────────────────
  const dragGrip = (grip: HTMLElement, isLeft: boolean): void => {
    grip.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      event.stopPropagation();
      silence();
      grip.classList.add('is-dragging');
      try {
        grip.setPointerCapture(event.pointerId);
      } catch {
        // Synthetic pointers have no capturable id; movement still works.
      }
      const move = (moveEvent: PointerEvent): void => {
        const fraction = fractionAt(moveEvent.clientX);
        const min = minPart();
        if (isLeft) start = Math.min(fraction, end - min);
        else end = Math.max(fraction, start + min);
        refresh();
      };
      const up = (): void => {
        grip.classList.remove('is-dragging');
        grip.removeEventListener('pointermove', move);
        grip.removeEventListener('pointerup', up);
        grip.removeEventListener('pointercancel', up);
        report();
        renderTransport();
      };
      grip.addEventListener('pointermove', move);
      grip.addEventListener('pointerup', up);
      grip.addEventListener('pointercancel', up);
    });
  };
  dragGrip(gripLeft, true);
  dragGrip(gripRight, false);

  // Dragging the selected region slides the whole interval without changing its
  // length, matching the interaction people already know from audio cutters. A
  // tap that never turns into a drag moves the playhead instead. Vertical
  // swipes stay the page's (touch-action: pan-y in the stylesheet).
  region.addEventListener('pointerdown', (event) => {
    const width = end - start;
    const grabOffset = fractionAt(event.clientX) - start;
    const downX = event.clientX;
    let dragging = false;
    const move = (moveEvent: PointerEvent): void => {
      if (!dragging) {
        if (Math.abs(moveEvent.clientX - downX) < 6) return;
        dragging = true;
        silence();
        try {
          region.setPointerCapture(moveEvent.pointerId);
        } catch {
          // See the grips.
        }
      }
      const next = clamp(fractionAt(moveEvent.clientX) - grabOffset, 0, 1 - width);
      start = next;
      end = next + width;
      refresh();
    };
    const finish = (): void => {
      region.removeEventListener('pointermove', move);
      region.removeEventListener('pointerup', up);
      region.removeEventListener('pointercancel', cancel);
    };
    const up = (upEvent: PointerEvent): void => {
      finish();
      if (dragging) {
        report();
        renderTransport();
      } else seekTo(fractionAt(upEvent.clientX));
    };
    const cancel = (): void => {
      finish();
      if (dragging) report();
    };
    region.addEventListener('pointermove', move);
    region.addEventListener('pointerup', up);
    region.addEventListener('pointercancel', cancel);
  });

  // A tap on the wave outside the selection parks the playhead there too — the
  // shopper can audition any spot before moving the handles to it.
  canvas.addEventListener('pointerdown', (event) => {
    const downX = event.clientX;
    const downY = event.clientY;
    const up = (upEvent: PointerEvent): void => {
      canvas.removeEventListener('pointerup', up);
      canvas.removeEventListener('pointercancel', cancel);
      if (Math.abs(upEvent.clientX - downX) < 6 && Math.abs(upEvent.clientY - downY) < 6) {
        seekTo(fractionAt(upEvent.clientX));
      }
    };
    const cancel = (): void => {
      canvas.removeEventListener('pointerup', up);
      canvas.removeEventListener('pointercancel', cancel);
    };
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', cancel);
  });

  const setZoom = (next: number): void => {
    zoom = clamp(next, 1, 16);
    refresh();
    zoomOut.disabled = zoom <= 1;
    zoomIn.disabled = zoom >= 16;
    const middle = xAt((start + end) / 2);
    scroller.scrollLeft = Math.max(0, middle - scroller.clientWidth / 2);
    if (!playhead.hidden && parked !== null) showPlayhead(parked);
  };
  zoomIn.addEventListener('click', () => setZoom(zoom * 2));
  zoomOut.addEventListener('click', () => setZoom(zoom / 2));

  // ── Effects ───────────────────────────────────────────────────────────────
  const publishEffects = (): void => {
    fadeIn.classList.toggle('is-active', effectsState.fadeInMs > 0);
    fadeOut.classList.toggle('is-active', effectsState.fadeOutMs > 0);
    fadeIn.setAttribute('aria-pressed', String(effectsState.fadeInMs > 0));
    fadeOut.setAttribute('aria-pressed', String(effectsState.fadeOutMs > 0));
    fadeInCtl.wrap.classList.toggle('is-active', effectsState.fadeInMs > 0);
    fadeOutCtl.wrap.classList.toggle('is-active', effectsState.fadeOutMs > 0);
    if (effectsState.fadeInMs > 0 && fadeChoices.includes(effectsState.fadeInMs)) {
      fadeInCtl.select.value = String(effectsState.fadeInMs);
    }
    if (effectsState.fadeOutMs > 0 && fadeChoices.includes(effectsState.fadeOutMs)) {
      fadeOutCtl.select.value = String(effectsState.fadeOutMs);
    }
    volumeInput.value = String(effectsState.volumePercent);
    volumeValue.textContent = `${effectsState.volumePercent}%`;
    applyGain();
    // The wave mirrors the effects, so redrawing here is what makes a fade
    // visibly ramp the bars the moment it is toggled.
    draw();
    options.onEffects?.({ ...effectsState });
  };
  fadeIn.addEventListener('click', () => {
    effectsState = { ...effectsState, fadeInMs: effectsState.fadeInMs > 0 ? 0 : Number(fadeInCtl.select.value) };
    publishEffects();
  });
  fadeOut.addEventListener('click', () => {
    effectsState = { ...effectsState, fadeOutMs: effectsState.fadeOutMs > 0 ? 0 : Number(fadeOutCtl.select.value) };
    publishEffects();
  });
  fadeInCtl.select.addEventListener('change', () => {
    if (effectsState.fadeInMs > 0) {
      effectsState = { ...effectsState, fadeInMs: Number(fadeInCtl.select.value) };
      publishEffects();
    }
  });
  fadeOutCtl.select.addEventListener('change', () => {
    if (effectsState.fadeOutMs > 0) {
      effectsState = { ...effectsState, fadeOutMs: Number(fadeOutCtl.select.value) };
      publishEffects();
    }
  });
  volumeInput.addEventListener('input', () => {
    effectsState = { ...effectsState, volumePercent: Number(volumeInput.value) };
    publishEffects();
  });

  // ── The song's own file, the other player, the line under the wave ────────
  const setStatus = (next: CutterStatus | null): void => {
    status = next;
    const busy = next?.tone === 'busy';
    // On its way: a shimmer on the stand-in wave and one calm line under it.
    // The worker's percent only moves the thin bar — a counting number reads
    // as something to wait for, and nothing here is.
    veil.hidden = !busy || peaks !== null;
    coming.hidden = !busy;
    comingText.textContent = busy ? next!.text || TEXTS.waveComing : '';
    comingBar.hidden = !busy || next!.percent == null;
    comingFill.style.width = `${busy && next!.percent != null ? Math.round(clamp(next!.percent, 0, 100)) : 0}%`;
    note.hidden = !next || busy;
    note.textContent = next && !busy ? next.text : '';
    note.className = `pz-cut-note${next?.tone === 'done' ? ' is-done' : ''}`;
    renderTransport();
  };

  const setAudio = async (next: CutterAudio): Promise<boolean> => {
    if (destroyed) return false;
    let nextPeaks = next.peaks?.length ? next.peaks : null;
    let nextDuration = next.durationMs && next.durationMs > 0 ? next.durationMs : 0;
    let url = next.audioUrl;
    let revoke = false;
    if (!nextPeaks || !nextDuration) {
      const blob =
        next.blob ??
        (url ? await fetch(url).then((response) => (response.ok ? response.blob() : null)).catch(() => null) : null);
      if (!blob || destroyed) return false;
      const decoded = await decodeBlob(blob);
      if (!decoded || destroyed) return false;
      nextPeaks = decoded.peaks;
      nextDuration = decoded.durationMs;
      if (!url) {
        url = URL.createObjectURL(blob);
        revoke = true;
      }
    }
    if (!url) return false;
    // The part stays where it is in TIME: the file's length differs from the
    // search result's by a little, and fractions would shift both ends.
    const wasWhole = isWhole();
    const fromMs = msAt(start);
    const toMs = end >= 1 - EPS ? null : msAt(end);
    const parkedMs = parked === null ? null : msAt(parked);
    durationMs = nextDuration;
    peaks = nextPeaks;
    start = clamp(fromMs / durationMs, 0, 1 - minPart());
    end = toMs === null ? 1 : clamp(toMs / durationMs, start + minPart(), 1);
    if (parkedMs !== null) parked = clamp(parkedMs / durationMs, 0, 1);
    file?.destroy();
    file = createFilePlayer(url, revoke, onSourceChange);
    track.classList.add('is-arriving');
    window.setTimeout(() => track.classList.remove('is-arriving'), 700);
    buildLengths();
    for (const relabel of relabels) relabel();
    if (transport?.playing()) switchWhenIdle = true;
    else takeOver();
    refresh();
    if (parked !== null && !playhead.hidden) showPlayhead(parked);
    setStatus(status?.tone === 'busy' ? null : status);
    // An end that was at the song's end is at the FILE's end now.
    if (!wasWhole) report(false);
    return true;
  };

  // Space plays and pauses, the way every audio editor behaves. The listener
  // is global because clicks land on non-focusable wave parts; of the cutters
  // on screen, the one the shopper touched (or opened) last owns the key — one
  // folded away or out of sight never does — and typing fields keep their spaces.
  const cutterToken = {
    shown: (): boolean => !destroyed && root.isConnected && root.getClientRects().length > 0,
  };
  claimSpace(cutterToken);
  root.addEventListener('pointerdown', () => claimSpace(cutterToken), true);
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== ' ' && event.code !== 'Space') return;
    if (destroyed || spaceOwner() !== cutterToken) return;
    const target = event.composedPath()[0] as HTMLElement | undefined;
    const tag = (target?.tagName ?? '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || tag === 'button' || target?.isContentEditable) {
      return;
    }
    event.preventDefault();
    if (playingNow()) pauseSource();
    else void startPlayback();
  };
  document.addEventListener('keydown', onKeyDown);

  if (loaded) file = createFilePlayer(loaded.url, loaded.revoke, onSourceChange);
  setTransport(options.transport ?? null);
  buildLengths();

  // Element sizes are zero until insertion, and the cutter moves house: the
  // wizard re-parents the whole form into its dialog, which changes the width
  // the wave is drawn against. `observeSize` watches the element itself (so the
  // move is just another size change) and falls back to the window's resize
  // where ResizeObserver is missing, rather than leaving a wave drawn to a
  // width that no longer exists.
  window.setTimeout(refresh, 0);
  const stopObserving = observeSize(scroller, refresh);
  publishEffects();
  setZoom(1);
  renderTransport();
  // The first stretch of a sound too long for the product is the answer until
  // the shopper chooses otherwise; the note stays until they do.
  if (fittedNote) report(false);

  return {
    element: root,
    pause: pauseSource,
    setAudio,
    setTransport,
    setStatus,
    hasAudio: () => file !== null,
    setWhole: () => {
      if (!wholeFits(durationMs, maxMs)) return false;
      silence();
      start = 0;
      end = 1;
      refresh();
      report();
      renderTransport();
      return true;
    },
    wholeFits: () => wholeFits(durationMs, maxMs),
    relayout: () => {
      if (destroyed) return;
      // Brought into view ("Iseci deo"): the cutter the shopper is looking at owns the spacebar.
      claimSpace(cutterToken);
      refresh();
      if (!playhead.hidden && parked !== null) showPlayhead(parked);
    },
    destroy: () => {
      destroyed = true;
      document.removeEventListener('keydown', onKeyDown);
      releaseSpace(cutterToken);
      stopObserving();
      unsubscribe?.();
      unsubscribe = null;
      file?.destroy();
      file = null;
      stopTicking();
      root.remove();
    },
  };
}
